import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Search, 
  Filter,
  CreditCard,
  Building,
  ShieldCheck,
  ShieldAlert,
  Clock,
  TrendingUp,
  CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adminAPI, partnerAPI } from '../../../services/api';

const AdminPartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerReferrals, setPartnerReferrals] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchPartnerReferrals(selectedPartner._id);
    }
  }, [selectedPartner]);

  const fetchPartners = async () => {
    try {
      // We'll use the existing user management or create a specific one
      // For now, let's assume we can filter users by role 'partner'
      const res = await adminAPI.getUsers(); // Generic user fetch
      if (res.data.success) {
        setPartners(res.data.data.filter(u => u.role === 'partner'));
      }
    } catch (error) {
      toast.error('Failed to fetch partners');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerReferrals = async (id) => {
    setLoadingReferrals(true);
    try {
      const res = await adminAPI.getPartnerReferrals(id);
      if (res.data.success) {
        setPartnerReferrals(res.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch referrals');
    } finally {
      setLoadingReferrals(false);
    }
  };

  const handleUpdateReferralStatus = async (orderId, status) => {
    try {
      const res = await adminAPI.updateReferralStatus(orderId, status);
      if (res.data.success) {
        toast.success(`Commission marked as ${status}`);
        fetchPartnerReferrals(selectedPartner._id);
      }
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleVerify = async (partnerId, status) => {
    try {
      const res = await adminAPI.updateUser(partnerId, { isVerified: status });
      if (res.data.success) {
        toast.success(status ? 'Partner Verified!' : 'Partner Unverified');
        fetchPartners();
        if (selectedPartner?._id === partnerId) {
          setSelectedPartner(prev => ({ ...prev, isVerified: status }));
        }
      }
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  const filteredPartners = partners.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Partner Management</h2>
          <p className="text-gray-500 dark:text-gray-400">Manage, verify, and track settlements for your business partners.</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search partners..." 
              className="pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 w-full md:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
             <Filter size={20} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Partner</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Referral Code</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredPartners.map((partner) => (
                  <tr 
                    key={partner._id} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${selectedPartner?._id === partner._id ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`}
                    onClick={() => setSelectedPartner(partner)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mr-3">
                          {partner.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900 dark:text-white">{partner.name}</p>
                          <p className="text-xs text-gray-500">{partner.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono font-bold text-blue-600">
                      {partner.referralCode || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      {partner.isVerified ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="w-3 h-3 mr-1" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 text-gray-400 hover:text-blue-600">
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredPartners.length === 0 && !loading && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-400">No partners found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details Panel */}
        <div className="space-y-6">
          {selectedPartner ? (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 border-b pb-4">Partner Details</h3>
              
              <div className="space-y-6">
                {/* Bank Account */}
                <div>
                  <div className="flex items-center text-blue-600 mb-3">
                    <Building className="mr-2" size={18} />
                    <h4 className="font-semibold">Bank Settlement</h4>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Account No:</span>
                      <span className="font-mono font-bold text-gray-900 dark:text-white">{selectedPartner.bankAccount?.accountNumber || 'Not provided'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">IFSC Code:</span>
                      <span className="font-mono font-bold text-gray-900 dark:text-white">{selectedPartner.bankAccount?.ifscCode || 'Not provided'}</span>
                    </div>
                  </div>
                </div>

                {/* Identity Verification */}
                <div>
                  <div className="flex items-center text-orange-600 mb-3">
                    <ShieldCheck className="mr-2" size={18} />
                    <h4 className="font-semibold">Identity Verification</h4>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl text-center">
                    {selectedPartner.documents?.aadhaarPhoto ? (
                      <div className="space-y-4">
                        <img 
                          src={selectedPartner.documents.aadhaarPhoto} 
                          alt="ID Proof" 
                          className="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-zoom-in"
                          onClick={() => window.open(selectedPartner.documents.aadhaarPhoto, '_blank')}
                        />
                        <p className="text-xs text-gray-500 italic">Click to view full identity proof</p>
                      </div>
                    ) : (
                      <div className="py-8 text-gray-400 flex flex-col items-center">
                        <ShieldAlert size={32} className="mb-2" />
                        <p className="text-sm">No ID proof uploaded</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center text-purple-600 mb-4 justify-between">
                    <div className="flex items-center">
                      <TrendingUp className="mr-2" size={18} />
                      <h4 className="font-semibold">Referral History</h4>
                    </div>
                    <span className="text-xs bg-purple-50 px-2 py-1 rounded-lg font-bold">
                      {partnerReferrals.length} Orders
                    </span>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm">
                    {loadingReferrals ? (
                      <div className="p-12 text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        <p className="text-sm text-gray-500">Loading order data...</p>
                      </div>
                    ) : partnerReferrals.length > 0 ? (
                      <div className="overflow-x-auto max-h-[400px]">
                        <table className="w-full text-left text-xs">
                          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900/50 z-10">
                            <tr>
                              <th className="px-4 py-3 font-bold text-gray-400 uppercase">Order</th>
                              <th className="px-4 py-3 font-bold text-gray-400 uppercase">Customer</th>
                              <th className="px-4 py-3 font-bold text-gray-400 uppercase">Amount</th>
                              <th className="px-4 py-3 font-bold text-gray-400 uppercase">Settlement</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                            {partnerReferrals.map(order => (
                              <tr key={order._id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
                                <td className="px-4 py-3">
                                  <p className="font-bold text-gray-900 dark:text-white">{order.orderNumber}</p>
                                  <p className="text-[10px] text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-gray-600 dark:text-gray-400">{order.customerInfo?.name || 'Unknown'}</span>
                                </td>
                                <td className="px-4 py-3 font-bold">₹{order.totalAmount * 0.2}</td>
                                <td className="px-4 py-3">
                                  {order.referralCommissionStatus === 'paid' ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                                      PAID
                                    </span>
                                  ) : (
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUpdateReferralStatus(order._id, 'paid');
                                      }}
                                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-all shadow-sm"
                                    >
                                      PAY
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-12 text-center text-gray-400">
                        <TrendingUp size={32} className="mx-auto mb-2 opacity-20" />
                        <p className="text-sm">No referrals found for this partner</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100 dark:border-gray-700 flex flex-col space-y-3">
                  {!selectedPartner.isVerified ? (
                    <button 
                      onClick={() => handleVerify(selectedPartner._id, true)}
                      className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-green-100 flex items-center justify-center"
                    >
                      <CheckCircle className="mr-2" size={18} />
                      Approve & Verify Profile
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleVerify(selectedPartner._id, false)}
                      className="w-full py-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-bold transition-all border border-red-100 flex items-center justify-center"
                    >
                      <XCircle className="mr-2" size={18} />
                      Revoke Verification
                    </button>
                  )}
                  <button className="w-full py-3 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl font-bold transition-all flex items-center justify-center">
                    <CreditCard className="mr-2" size={18} />
                    Record Settlement
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-12 text-center text-gray-400">
              <Users size={48} className="mx-auto mb-4 opacity-20" />
              <p>Select a partner to view details and verify profile.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPartnerManagement;
